import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { useQueueStore } from "@/lib/store";
import { useHospitalProfileStore } from "@/lib/stores/hospitalProfile";
import { useBillsStore } from "@/lib/stores/bills";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { RefreshCw } from "lucide-react";
import { AnalyticsHeader } from "@/components/analytics/AnalyticsHeader";
import { AnalyticsSummaryCards } from "@/components/analytics/AnalyticsSummaryCards";
import { RevenueCharts } from "@/components/analytics/RevenueCharts";
import { PatientCharts } from "@/components/analytics/PatientCharts";
import { AdvancedAnalytics } from "@/components/analytics/AdvancedAnalytics";
import { MonthlyRevenueChart } from "@/components/analytics/MonthlyRevenueChart";
import { MonthlyPatientsChart } from "@/components/analytics/MonthlyPatientsChart";
import { PatientAnalytics } from "@/components/analytics/PatientAnalytics";
import jsPDF from 'jspdf';

const Analytics = () => {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  
  const [activeTab, setActiveTab] = useState<'revenue' | 'patients' | 'monthly-revenue' | 'monthly-patients' | 'advanced' | 'patient-analytics'>('revenue');
  const [selectedDataType, setSelectedDataType] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { 
    getRevenueSummary, 
    getPatientCountByDoctor,
    getCompletedAppointmentsByDate,
    fetchPatients,
    fetchDoctors,
    patients,
    doctors
  } = useQueueStore();
  
  const { profile: hospitalProfile } = useHospitalProfileStore();
  const { fetchBills } = useBillsStore();
  
  // Fetch data when component mounts and set up periodic refresh
  useEffect(() => {
    console.log('Analytics: Component mounted, fetching data...');
    fetchPatients();
    fetchDoctors();
    fetchBills(); // Fetch bills for revenue calculations
    
    // Set up periodic refresh every 30 seconds to keep data current
    const interval = setInterval(() => {
      console.log('Analytics: Refreshing data...');
      fetchPatients();
      fetchBills(); // Refresh bills too
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchPatients, fetchDoctors, fetchBills]);
  
  // Manual refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('Analytics: Manual refresh triggered...');
      await fetchPatients();
      await fetchDoctors();
      await fetchBills(); // Refresh bills too
    } catch (error) {
      console.error('Error refreshing analytics data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Get financial data
  const { 
    totalRevenue, 
    doctorRevenue, 
    dateRevenue, 
    paymentMethodRevenue, 
    serviceRevenue, 
    presentMonthServiceRevenue, // Get present month service revenue
    previousMonthServiceRevenue, // Get previous month service revenue
    monthlyTrend, 
    revenueForecast,
    dailyRevenueData, // Get daily revenue data
    dailyPatientData, // Get daily patient data
    growthMetrics,
    retentionMetrics,
    doctorKPIs,
    timeAnalytics
  } = getRevenueSummary();
  const patientsByDoctor = getPatientCountByDoctor();
  const appointmentsByDate = getCompletedAppointmentsByDate(
    dateRange.from, 
    dateRange.to
  );
  
  // Format data for charts
  const doctorRevenueData = doctorRevenue.map(item => ({
    name: item.doctorName.replace('Dr. ', ''),
    value: item.revenue
  }));
  
  const dateRevenueData = dateRevenue.map(item => ({
    name: format(new Date(item.date), 'MMM d'),
    revenue: item.revenue,
  }));
  
  const patientCountData = patientsByDoctor.map(item => ({
    name: item.doctorName.replace('Dr. ', ''),
    patients: item.count
  }));
  
  const appointmentsChartData = appointmentsByDate.map(item => ({
    name: format(new Date(item.date), 'MMM d'),
    appointments: item.count
  }));

  // Calculate weekly distribution from time analytics
  const weeklyDistribution = timeAnalytics.peakDays.map(item => ({
    day: item.day,
    count: item.count
  }));

  // Calculate average daily patients
  const averageDailyPatients = appointmentsByDate.length 
    ? (appointmentsByDate.reduce((sum, item) => sum + item.count, 0) / appointmentsByDate.length)
    : 0;

  // Calculate today's revenue
  const today = new Date().toISOString().split('T')[0];
  const todayRevenue = dateRevenue
    .filter(item => item.date === today)
    .reduce((sum, item) => sum + item.revenue, 0);

  // Calculate current month's revenue
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
  const currentMonthData = monthlyTrend.find(month => 
    month.month === new Date(currentMonth + '-01').toLocaleString('default', { month: 'short', year: 'numeric' })
  );
  const monthlyRevenue = currentMonthData?.revenue || 0;

  // Debug information
  console.log('Analytics Debug Info:', {
    totalRevenue,
    todayRevenue,
    doctorRevenue: doctorRevenue.length,
    dateRevenue: dateRevenue.length,
    patientsByDoctor: patientsByDoctor.length,
    appointmentsByDate: appointmentsByDate.length,
    averageDailyPatients,
    weeklyDistribution: weeklyDistribution.length
  });

  // Enhanced download report function
  const downloadReport = () => {
    if (!selectedDataType || !selectedFormat) return;

    const dateRangeStr = dateRange.from && dateRange.to 
      ? `-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}`
      : `-${format(new Date(), 'yyyy-MM-dd')}`;

    if (selectedFormat === 'csv') {
      downloadCSV(selectedDataType, dateRangeStr);
    } else if (selectedFormat === 'pdf') {
      downloadPDF(selectedDataType, dateRangeStr);
    }
  };

  const downloadCSV = (dataType: string, dateRangeStr: string) => {
    let csvContent = "data:text/csv;charset=utf-8,";
    let filename = '';

    switch(dataType) {
      case 'revenue-summary':
        csvContent += "Doctor,Revenue,Patient Count\n";
        doctorRevenue.forEach(doctor => {
          const patientCount = patientsByDoctor.find(d => d.doctorId === doctor.doctorId)?.count || 0;
          csvContent += `${doctor.doctorName},${doctor.revenue},${patientCount}\n`;
        });
        filename = `revenue-summary${dateRangeStr}.csv`;
        break;

      case 'patient-details':
        csvContent += "Patient ID,Name,Age,Gender,Contact,Doctor,Amount Paid,Payment Method,Registration Date,Status\n";
        const filteredPatients = dateRange.from && dateRange.to 
          ? patients.filter(p => {
              const regDate = new Date(p.registeredAt);
              return regDate >= dateRange.from! && regDate <= dateRange.to!;
            })
          : patients;
        
        filteredPatients.forEach(patient => {
          const doctor = doctors.find(d => d.id === patient.doctorId);
          csvContent += `${patient.id},${patient.name},${patient.age},${patient.gender},${patient.contactNumber || 'N/A'},${doctor?.name || 'N/A'},${patient.amountPaid || 0},${patient.paymentMode || 'N/A'},${patient.registeredAt},${patient.status}\n`;
        });
        filename = `patient-details${dateRangeStr}.csv`;
        break;

      case 'doctor-performance':
        csvContent += "Doctor,Total Revenue,Patient Count,Average Revenue per Patient,Patient Satisfaction,Efficiency\n";
        doctorKPIs.forEach(doctor => {
          csvContent += `${doctor.doctorName},${doctor.avgRevenuePerPatient * (patientsByDoctor.find(d => d.doctorId === doctor.doctorId)?.count || 0)},${patientsByDoctor.find(d => d.doctorId === doctor.doctorId)?.count || 0},${doctor.avgRevenuePerPatient.toFixed(2)},${doctor.patientSatisfaction.toFixed(1)}%,${doctor.efficiency.toFixed(1)}%\n`;
        });
        filename = `doctor-performance${dateRangeStr}.csv`;
        break;

      case 'complete-report':
        csvContent += "Report Type,Data\n";
        csvContent += `Total Revenue,${totalRevenue}\n`;
        csvContent += `Total Patients,${patients.length}\n`;
        csvContent += `Completed Patients,${patients.filter(p => p.status === 'completed').length}\n`;
        csvContent += `Average Daily Patients,${averageDailyPatients.toFixed(1)}\n`;
        csvContent += `Today's Revenue,${todayRevenue}\n`;
        csvContent += `Monthly Revenue,${monthlyRevenue}\n`;
        csvContent += `Revenue Growth,${growthMetrics.revenueGrowth.toFixed(1)}%\n`;
        csvContent += `Patient Growth,${growthMetrics.patientGrowth.toFixed(1)}%\n`;
        csvContent += `Retention Rate,${retentionMetrics.retentionRate.toFixed(1)}%\n`;
        csvContent += `Returning Patients,${retentionMetrics.returningPatients}\n`;
        csvContent += `Average Wait Time,${timeAnalytics.avgWaitTime.toFixed(1)} days\n`;
        csvContent += "\nDoctor Performance:\n";
        csvContent += "Doctor,Revenue,Patients,Avg Revenue per Patient\n";
        doctorRevenue.forEach(doctor => {
          const patientCount = patientsByDoctor.find(d => d.doctorId === doctor.doctorId)?.count || 0;
          csvContent += `${doctor.doctorName},${doctor.revenue},${patientCount},${patientCount > 0 ? (doctor.revenue / patientCount).toFixed(2) : 0}\n`;
        });
        filename = `complete-report${dateRangeStr}.csv`;
        break;
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = (dataType: string, dateRangeStr: string) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Header
    doc.setFontSize(18);
    doc.text(`${hospitalProfile.name} - Analytics Report`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    doc.setFontSize(12);
    doc.text(`Report Type: ${dataType.replace('-', ' ').toUpperCase()}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`, 20, yPosition);
    yPosition += 8;
    
    if (dateRange.from && dateRange.to) {
      doc.text(`Date Range: ${format(dateRange.from, 'yyyy-MM-dd')} to ${format(dateRange.to, 'yyyy-MM-dd')}`, 20, yPosition);
      yPosition += 8;
    }

    yPosition += 10;

    switch(dataType) {
      case 'revenue-summary':
        doc.setFontSize(16);
        doc.text('Revenue Summary by Doctor', 20, yPosition);
        yPosition += 15;

        doc.setFontSize(10);
        doc.text('Doctor', 20, yPosition);
        doc.text('Revenue (₹)', 80, yPosition);
        doc.text('Patient Count', 130, yPosition);
        yPosition += 8;

        // Draw line
        doc.line(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 5;

        doctorRevenue.forEach(doctor => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          const patientCount = patientsByDoctor.find(d => d.doctorId === doctor.doctorId)?.count || 0;
          doc.text(doctor.doctorName, 20, yPosition);
          doc.text(doctor.revenue.toLocaleString('en-IN'), 80, yPosition);
          doc.text(patientCount.toString(), 130, yPosition);
          yPosition += 8;
        });
        break;

      case 'patient-details':
        doc.setFontSize(16);
        doc.text('Patient Details', 20, yPosition);
        yPosition += 15;

        doc.setFontSize(8);
        doc.text('ID', 20, yPosition);
        doc.text('Name', 40, yPosition);
        doc.text('Age', 80, yPosition);
        doc.text('Gender', 95, yPosition);
        doc.text('Doctor', 110, yPosition);
        doc.text('Amount', 150, yPosition);
        doc.text('Contact', 170, yPosition);
        yPosition += 8;

        doc.line(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 5;

        const filteredPatients = dateRange.from && dateRange.to 
          ? patients.filter(p => {
              const regDate = new Date(p.registeredAt);
              return regDate >= dateRange.from! && regDate <= dateRange.to!;
            })
          : patients;

        filteredPatients.forEach(patient => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          const doctor = doctors.find(d => d.id === patient.doctorId);
          doc.text(patient.id.substring(0, 8), 20, yPosition);
          doc.text(patient.name.substring(0, 15), 40, yPosition);
          doc.text(patient.age.toString(), 80, yPosition);
          doc.text(patient.gender, 95, yPosition);
          doc.text((doctor?.name || 'N/A').substring(0, 15), 110, yPosition);
          doc.text((patient.amountPaid || 0).toString(), 150, yPosition);
          doc.text((patient.contactNumber || 'N/A').substring(0, 12), 170, yPosition);
          yPosition += 8;
        });
        break;

      case 'doctor-performance':
        doc.setFontSize(16);
        doc.text('Doctor Performance Metrics', 20, yPosition);
        yPosition += 15;

        doc.setFontSize(10);
        doc.text('Doctor', 20, yPosition);
        doc.text('Revenue', 80, yPosition);
        doc.text('Patients', 120, yPosition);
        doc.text('Avg/Patient', 150, yPosition);
        doc.text('Satisfaction', 180, yPosition);
        yPosition += 8;

        doc.line(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 5;

        doctorKPIs.forEach(doctor => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          const patientCount = patientsByDoctor.find(d => d.doctorId === doctor.doctorId)?.count || 0;
          const totalRevenue = doctor.avgRevenuePerPatient * patientCount;
          doc.text(doctor.doctorName, 20, yPosition);
          doc.text(totalRevenue.toLocaleString('en-IN'), 80, yPosition);
          doc.text(patientCount.toString(), 120, yPosition);
          doc.text(doctor.avgRevenuePerPatient.toFixed(2), 150, yPosition);
          doc.text(`${doctor.patientSatisfaction.toFixed(1)}%`, 180, yPosition);
          yPosition += 8;
        });
        break;

      case 'complete-report':
        doc.setFontSize(16);
        doc.text('Complete Analytics Report', 20, yPosition);
        yPosition += 15;

        doc.setFontSize(12);
        doc.text('Summary Statistics:', 20, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.text(`Total Revenue: ₹${totalRevenue.toLocaleString('en-IN')}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Total Patients: ${patients.length}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Completed Patients: ${patients.filter(p => p.status === 'completed').length}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Average Daily Patients: ${averageDailyPatients.toFixed(1)}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Today's Revenue: ₹${todayRevenue.toLocaleString('en-IN')}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Monthly Revenue: ₹${monthlyRevenue.toLocaleString('en-IN')}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Revenue Growth: ${growthMetrics.revenueGrowth.toFixed(1)}%`, 20, yPosition);
        yPosition += 8;
        doc.text(`Patient Growth: ${growthMetrics.patientGrowth.toFixed(1)}%`, 20, yPosition);
        yPosition += 8;
        doc.text(`Retention Rate: ${retentionMetrics.retentionRate.toFixed(1)}%`, 20, yPosition);
        yPosition += 8;
        doc.text(`Returning Patients: ${retentionMetrics.returningPatients}`, 20, yPosition);
        yPosition += 8;
        doc.text(`Average Wait Time: ${timeAnalytics.avgWaitTime.toFixed(1)} days`, 20, yPosition);
        yPosition += 15;

        doc.setFontSize(12);
        doc.text('Doctor Performance:', 20, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.text('Doctor', 20, yPosition);
        doc.text('Revenue', 80, yPosition);
        doc.text('Patients', 120, yPosition);
        doc.text('Avg/Patient', 150, yPosition);
        yPosition += 8;

        doc.line(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 5;

        doctorRevenue.forEach(doctor => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          const patientCount = patientsByDoctor.find(d => d.doctorId === doctor.doctorId)?.count || 0;
          doc.text(doctor.doctorName, 20, yPosition);
          doc.text(doctor.revenue.toLocaleString('en-IN'), 80, yPosition);
          doc.text(patientCount.toString(), 120, yPosition);
          doc.text(patientCount > 0 ? (doctor.revenue / patientCount).toFixed(2) : '0', 150, yPosition);
          yPosition += 8;
        });
        break;
    }

    const filename = `${dataType}${dateRangeStr}.pdf`;
    doc.save(filename);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <AnalyticsHeader 
                dateRange={dateRange} 
                setDateRange={setDateRange}
                downloadReport={downloadReport}
                selectedDataType={selectedDataType}
                setSelectedDataType={setSelectedDataType}
                selectedFormat={selectedFormat}
                setSelectedFormat={setSelectedFormat}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="ml-4"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            </div>
        
        {/* Show message if no data available */}
        {totalRevenue === 0 && doctorRevenue.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h2 className="text-2xl font-bold mb-2">No Analytics Data Available</h2>
            <p className="text-muted-foreground mb-4">
              Analytics will appear here once you have completed patient consultations and payments.
            </p>
            <div className="text-sm text-muted-foreground">
              <p>• Complete patient consultations to see revenue data</p>
              <p>• Process payments to track financial metrics</p>
              <p>• Assign patients to doctors to see doctor-specific analytics</p>
            </div>
          </div>
        )}
        
        {/* Show analytics only if there's data */}
        {(totalRevenue > 0 || doctorRevenue.length > 0) && (
          <>
            <AnalyticsSummaryCards 
              totalRevenue={totalRevenue}
              doctorName={doctorRevenue[0]?.doctorName.replace('Dr. ', '') || 'N/A'}
              doctorRevenue={doctorRevenue[0]?.revenue || 0}
              averageDailyPatients={averageDailyPatients}
              appointmentsCount={dateRevenue.length}
              todayRevenue={todayRevenue}
              monthlyRevenue={monthlyRevenue}
            />

            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'revenue' | 'patients' | 'monthly-revenue' | 'monthly-patients' | 'advanced' | 'patient-analytics')}>
              <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 max-w-full mb-6 w-full bg-gray-100/50 p-1.5 gap-1">
                <TabsTrigger 
                  value="revenue" 
                  className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
                >
                  <span className="hidden sm:inline">Revenue Analysis</span>
                  <span className="sm:hidden">Revenue</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="patients" 
                  className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
                >
                  <span className="hidden sm:inline">Patient Count</span>
                  <span className="sm:hidden">Patients</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="monthly-revenue" 
                  className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
                >
                  <span className="hidden sm:inline">Monthly Revenue</span>
                  <span className="sm:hidden">Monthly Rev</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="monthly-patients" 
                  className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
                >
                  <span className="hidden sm:inline">Monthly Patients</span>
                  <span className="sm:hidden">Monthly Pat</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="advanced" 
                  className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
                >
                  <span className="hidden sm:inline">Advanced Analytics</span>
                  <span className="sm:hidden">Advanced</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="patient-analytics" 
                  className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
                >
                  <span className="hidden sm:inline">Patient Analytics</span>
                  <span className="sm:hidden">Pat Analytics</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="revenue" className="space-y-6">
                <RevenueCharts 
                  doctorRevenueData={doctorRevenueData}
                  dateRevenueData={dateRevenueData}
                  doctorRevenue={doctorRevenue}
                  patientsByDoctor={patientsByDoctor}
                  paymentMethodRevenue={paymentMethodRevenue}
                />
              </TabsContent>
              
              <TabsContent value="patients" className="space-y-6">
                <PatientCharts 
                  patientCountData={patientCountData}
                  appointmentsChartData={appointmentsChartData}
                  weeklyDistribution={weeklyDistribution}
                />
              </TabsContent>
              
              <TabsContent value="monthly-revenue" className="space-y-6">
                <MonthlyRevenueChart 
                  monthlyTrend={monthlyTrend}
                  dailyRevenueData={dailyRevenueData}
                  serviceRevenue={serviceRevenue}
                  presentMonthServiceRevenue={presentMonthServiceRevenue}
                  previousMonthServiceRevenue={previousMonthServiceRevenue}
                />
              </TabsContent>
              
              <TabsContent value="monthly-patients" className="space-y-6">
                <MonthlyPatientsChart 
                  monthlyTrend={monthlyTrend}
                  dailyPatientData={dailyPatientData}
                />
              </TabsContent>
              
              <TabsContent value="advanced" className="space-y-6">
                <AdvancedAnalytics 
                  growthMetrics={growthMetrics}
                  retentionMetrics={retentionMetrics}
                  doctorKPIs={doctorKPIs}
                  timeAnalytics={timeAnalytics}
                />
              </TabsContent>
              
              <TabsContent value="patient-analytics" className="space-y-6">
                <PatientAnalytics />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Analytics;
